// Undo/redo for a board as an INVERSE PATCH, not a whole-board snapshot swap.
//
// Why: the board tab persists whole boards, and the records writer
// (extension_records_sync_scope) tombstones every record in the scope that is
// missing from what it is handed. Undo used to put the pre-edit SNAPSHOT back
// and mark it dirty, so any card a teammate added after that snapshot (it
// arrived through the WS fast path or a disk re-read) was missing from the
// write and got deleted for the whole team — and every field a teammate had
// changed on other cards was reverted, because the restore carried a fresh
// board updatedAt. (Card 4f6be6c3, code review 24.09 2D §2.1, Scenario A.)
//
// Now a history entry is the pair { before, after } of ONE edit. Undo applies
// the change after→before onto the CURRENT board, touching only the entities
// that edit changed; redo applies before→after. Everything else on the current
// board — including records that arrived since — is left exactly as it is, so
// the only record an undo can remove is one the undone edit itself created.
// The same idea as the WS-frame and MCP-write fixes (TaskBoardTab
// applyBoardRecords, mcp/handlers/index.js runHandler): keep what arrived in
// the snapshot that gets persisted.

import { compareGroupsByPosition } from './boardsStore';

// Keys of the board object that are not board-level "meta" fields.
const NON_META_KEYS = new Set(['groups', 'tasks', 'updatedAt']);
// A sentinel entity id meaning "a board-level field" (same one the realtime
// dirty tracking in TaskBoardTab and patchBoardWithRecords use).
export const META_ID = '__meta__';

function sameJson(a, b) {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function byId(list) {
  const map = new Map();
  for (const item of list || []) if (item && item.id != null) map.set(item.id, item);
  return map;
}

function changedKeys(a, b, skip) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  return [...keys].filter((k) => !(skip && skip.has(k)) && !sameJson(a?.[k], b?.[k]));
}

function metaChangedKeys(from, to) {
  return changedKeys(from, to, NON_META_KEYS);
}

/**
 * Ids of every task/group whose content differs between two versions of the
 * same board (created, deleted or modified), plus META_ID when a board-level
 * field changed. Order-independent.
 */
export function touchedEntityIds(from, to) {
  if (!from || !to) return [];
  const ids = [];
  for (const kind of ['groups', 'tasks']) {
    const a = byId(from[kind]);
    const b = byId(to[kind]);
    for (const id of new Set([...a.keys(), ...b.keys()])) {
      if (!sameJson(a.get(id), b.get(id))) ids.push(id);
    }
  }
  if (metaChangedKeys(from, to).length) ids.push(META_ID);
  return ids;
}

// Apply the change from→to of one entity list onto `current`'s list.
function patchList(currentList, fromList, toList, now) {
  const from = byId(fromList);
  const to = byId(toList);
  let out = currentList || [];
  let changed = false;
  const touched = [];
  const edit = () => {
    if (!changed) {
      out = [...out];
      changed = true;
    }
  };
  for (const id of new Set([...from.keys(), ...to.keys()])) {
    const a = from.get(id);
    const b = to.get(id);
    if (sameJson(a, b)) continue;
    const idx = out.findIndex((x) => x.id === id);
    if (!b) {
      // This direction removes the entity: the patched edit created it (undo)
      // or deleted it (redo). The ONLY way a patch removes a record.
      if (idx === -1) continue;
      edit();
      out.splice(idx, 1);
      touched.push(id);
    } else if (!a) {
      // This direction (re)creates the entity: undoing a delete, redoing a create.
      const restored = 'updatedAt' in b ? { ...b, updatedAt: now } : b;
      edit();
      if (idx === -1) out.push(restored);
      else out[idx] = restored;
      touched.push(id);
    } else {
      // Modified by the edit: revert only the fields the edit changed, on the
      // CURRENT version, so a teammate's change to another field survives. An
      // entity deleted since (by someone else) is not resurrected.
      if (idx === -1) continue;
      const keys = changedKeys(a, b, new Set(['updatedAt']));
      if (!keys.length) continue;
      const next = { ...out[idx] };
      for (const k of keys) {
        if (b[k] === undefined) delete next[k];
        else next[k] = b[k];
      }
      if ('updatedAt' in next || 'updatedAt' in b) next.updatedAt = now;
      edit();
      out[idx] = next;
      touched.push(id);
    }
  }
  return { list: out, changed, touched };
}

/**
 * Apply the change `from` → `to` (two versions of the same board, one history
 * step apart) onto `current`. Undo = applyBoardPatch(current, after, before);
 * redo = applyBoardPatch(current, before, after).
 *
 * Returns { board, touched }: the new board (a fresh object with a fresh
 * updatedAt) and the entity ids it changed (for the realtime dirty tracking).
 * When nothing applies — every entity the step touched has since been deleted
 * by someone else — it returns `current` itself and `touched` is empty, so the
 * caller writes nothing (a fresh board stamp over stale meta could otherwise
 * revert a remote board rename).
 */
export function applyBoardPatch(current, from, to, now = new Date().toISOString()) {
  if (!current) return { board: current, touched: [] };
  const groups = patchList(current.groups, from?.groups, to?.groups, now);
  const tasks = patchList(current.tasks, from?.tasks, to?.tasks, now);
  const next = { ...current, groups: groups.list, tasks: tasks.list, updatedAt: now };
  const touched = [...groups.touched, ...tasks.touched];
  const metaKeys = metaChangedKeys(from, to);
  for (const k of metaKeys) {
    if (to?.[k] === undefined) delete next[k];
    else next[k] = to[k];
  }
  if (metaKeys.length) touched.push(META_ID);
  if (!touched.length) return { board: current, touched };
  if (groups.changed) next.groups = [...next.groups].sort(compareGroupsByPosition);
  return { board: next, touched };
}
